/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  OperatorProp,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import {
  baseSearchFieldProps,
  updateSearchFieldDataType,
} from '../SearchTextField/SearchTextFieldManager';
import { defaultValue } from './SearchCheckbox';

const SearchCheckboxManager: ComponentManagerFn<'SearchCheckbox'> = (
  platform: Platform,
  type: 'SearchCheckbox',
): ComponentManager<'SearchCheckbox'> => {
  return {
    ...baseComponentManager<'SearchCheckbox'>(platform, 'SearchCheckbox'),
    type: 'SearchCheckbox',
    defaultValue,
    props: {
      ...baseSearchFieldProps,
      viewAttribute: {
        ...(baseSearchFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => {
          return ['TF', 'YN', 'Boolean'];
        },
      },
      defaultOperator: {
        ...(baseSearchFieldProps.defaultOperator as OperatorProp),
        getDataType: async () => {
          return 'TF';
        },
      },
    },
    isPropertyVisible(prop, itemData, context): boolean {
      switch (prop) {
        case 'dataType':
        case 'updatable':
        case 'insertable': {
          return false;
        }
        default:
          return true;
      }
    },
    onPropertyChange: (property, value, itemProps, context) => {
      if (property === 'viewAttribute') {
        updateSearchFieldDataType(type, value, context);
      }
      return { [property]: value };
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    replaceableWith: [
      'SearchCheckbox',
      'SearchLookupField',
      'SearchSelectField',
      'SearchTextField',
      'SearchNumberField',
    ],
    name: 'Search Checkbox',
    devStyles: { display: 'inline-flex' },
    category: 'Search',
    icon: { iconName: 'check-square' },
    description: `**Overview**<br/>
The Search Checkbox is a specialized input component designed for **Search Forms**. It allows users to filter search results based on boolean (Yes/No, True/False) criteria.
Use this component when you want to let users find records that match a specific binary state, such as "Is Active?", "Has Attachment?", or "Approved?".

**1. Data Compatibility**<br/>
* **View Attribute:** This component strictly binds to boolean-like data types in your Data Store to ensure search query compatibility:
    * **TF:** True/False values.
    * **YN:** Yes/No values.
    * **Boolean:** Standard system boolean values.

**2. Configuration**<br/>
* **Inherited Properties:** It inherits standard search field properties (such as **Label**, **Placeholder**, and **Help Text**) via the base search configuration.
* **Default Operator:** The component automatically sets the search operator relevant to boolean logic (typically **"Equals"**), simplifying configuration by removing the need to manually define mathematical operators.

**3. Behavior**<br/>
* **Read-Only Context:** Unlike standard form checkboxes used for data entry, search fields are used exclusively to query data. Therefore, properties like **Updatable** and **Insertable** are disabled by default.
* **Interchangeability:** It is part of the Search component family. It can be easily swapped with other search inputs like **Search Select Field** or **Search Text Field** if the underlying data requirements change (for example, if a "Yes/No" field is converted into a multi-status dropdown).

**4. Best Use Cases**<br/>
* **Status Filters:** Adding a "Show Archived Only" or "Is Active" checkbox to a project list to quickly narrow down relevant records.
* **Feature Flags:** Searching for all users who have specific permissions or "Premium Membership" enabled.
* **Compliance & Auditing:** Filtering a list of documents to find those specifically marked as "Confidential" or "Signed."
* **System Health:** Filtering an IoT device list to show only those currently marked as "Offline."`,
  };
};

export default SearchCheckboxManager;
